import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
var _excluded = ["className", "classes", "disabled", "onClick", "handleClose", "variant", "hintText", "headerDivider", "children"];
import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import makeStyles from './../makeStyles';
import MenuItem from '@material-ui/core/MenuItem';
import { spacing30, spacing40, lineHeightParagraphMedium } from '../styles/tokens';
import ListItemText from '../List/ListItemText';
var useStyles = makeStyles(function (theme) {
  return {
    root: {
      // Since menu list item is pulling from MUI, have to override to match recommended list
      textOverflow: 'ellipsis',
      justifyContent: 'space-between',
      '& span': {
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.fontSize,
        fontWeight: theme.typography.fontWeightRegular,
        lineHeight: lineHeightParagraphMedium
      },
      '&.padding': {
        padding: spacing40 + " 1.25rem",
        display: 'flex'
      },
      '&:hover, &:focus': {
        color: theme.palette.grey[600],
        backgroundColor: theme.palette.grey[250]
      },
      '&:active, &:hover&:active': {
        backgroundColor: theme.palette.ctaColor.active,
        color: theme.palette.grey['100'],
        '& svg': {
          fill: theme.palette.grey['100']
        }
      },
      '& svg': {
        marginRight: spacing30,
        top: '0.1rem',
        position: 'relative'
      },
      '&$header': {
        opacity: 1
      }
    },
    header: {
      color: theme.palette.grey[600],
      whiteSpace: 'normal',
      overflowWrap: 'break-word',
      pointerEvents: 'none',
      '&.padding': {
        padding: spacing40 + " 1.25rem",
        display: 'flex'
      },
      '& svg': {
        marginRight: spacing30,
        top: '0.1rem',
        position: 'relative'
      },
      '& span': {
        display: 'flex',
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.fontSize,
        fontWeight: theme.typography.fontWeightBold,
        lineHeight: lineHeightParagraphMedium
      }
    },
    headerBorder: {
      borderTop: "solid 1px " + theme.palette.grey[300]
    },
    hintText: {
      textAlign: 'right',
      color: theme.palette.grey[500]
    }
  };
});
/**
 * An item in an ActionMenu.
 */

var ActionMenuItem = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _cn;

  var localClasses = useStyles();

  var className = props.className,
      classes = props.classes,
      disabled = props.disabled,
      _onClick = props.onClick,
      handleClose = props.handleClose,
      variant = props.variant,
      hintText = props.hintText,
      headerDivider = props.headerDivider,
      children = props.children,
      rest = _objectWithoutPropertiesLoose(props, _excluded);

  return variant && variant === 'Header' ? /*#__PURE__*/React.createElement(MenuItem, _extends({}, rest, {
    className: cn(localClasses.root, className, 'padding', (_cn = {}, _cn[localClasses.headerBorder] = headerDivider, _cn), localClasses.header),
    disabled: true,
    ref: ref
  }), /*#__PURE__*/React.createElement(ListItemText, null, children)) : /*#__PURE__*/React.createElement(MenuItem, _extends({}, rest, {
    className: cn(localClasses.root, className, 'padding'),
    disabled: disabled,
    onClick: function onClick(e) {
      _onClick && _onClick(e);
      handleClose && handleClose(e);
    },
    classes: classes,
    ref: ref
  }), /*#__PURE__*/React.createElement(ListItemText, null, children), hintText ? /*#__PURE__*/React.createElement(ListItemText, {
    className: localClasses.hintText
  }, hintText) : null);
});
ActionMenuItem.muiName = 'ActionMenuItem';
ActionMenuItem.displayName = 'ActionMenuItem';
ActionMenuItem.propTypes = {
  /**
   * The content of the component.
   */
  children: PropTypes.node.isRequired,

  /**
   * @ignore
   *
   * Override or extend the styles applied to the component.
   */
  classes: PropTypes.object,

  /**
   * If `true`, the action menu item will be disabled.
   */
  disabled: PropTypes.bool,

  /**
   * Callback triggered when action menu item is clicked.
   */
  onClick: PropTypes.func,

  /**
   * Type of ActionMenuItem
   */
  variant: PropTypes.oneOf(['Item', 'Header']),

  /**
   *  Provides supporting text beside the main text.
   */
  hintText: PropTypes.string,

  /**
   * Show a divider before an ActionMenuItem, only available if the variant is set to Header.
   */
  headerDivider: PropTypes.bool
};
ActionMenuItem.defaultProps = {
  disabled: false,
  variant: 'Item',
  headerDivider: true
};
export default ActionMenuItem;